import {
  getInvoices, generateInvoice, updateInvoice, redateInvoice,
  getInvoiceSettings, updateInvoiceSettings, invoicePdfUrl, getUsers,
} from '../admin-api';
import type { InvoiceRow, InvoicePatch, InvoiceSettings, AdminUser } from '../admin-api';
import { esc, adminTable, pagination, badge, formatDate, formatCents, formField, adminSelect, searchBar } from '../admin-ui';
import { note, scopedHelpers } from '../admin-helpers';
import { cmsErrorText } from './admin-cms';

/**
 * Invoices (reworked 2026-10-14): list, issue by hand, correct, re-date,
 * download, and the numbering settings. Was a read-only table.
 *
 * What an issued invoice may change is the server's rule (game/admin
 * GameAdminEditService.updateInvoice); `isManual` mirrors it only so the form
 * does not offer a Status the server would refuse.
 */

const MONEY_LINKS = ['orderId', 'paymentId', 'withdrawalId', 'refundId'];

export function isManual(r: InvoiceRow): boolean {
  if (r.paymentOrderRef || r.withdrawalId || r.creditNoteForInvoiceId) return false;
  const meta = (r.metadata ?? {}) as Record<string, unknown>;
  return !MONEY_LINKS.some((k) => meta[k] != null);
}

/** Editable on any invoice: who it was issued to. */
export const RECIPIENT_FIELDS: Array<{ key: keyof InvoicePatch; label: string }> = [
  { key: 'recipientName', label: 'Recipient name' },
  { key: 'recipientCompany', label: 'Recipient company' },
  { key: 'recipientCode', label: 'Company / VAT code' },
  { key: 'paymentMethod', label: 'Payment method' },
  { key: 'billingFirstName', label: 'Billing first name' },
  { key: 'billingLastName', label: 'Billing last name' },
  { key: 'billingLine1', label: 'Billing address' },
  { key: 'billingLine2', label: 'Billing address 2' },
  { key: 'billingCity', label: 'Billing city' },
  { key: 'billingZip', label: 'Billing postcode' },
  { key: 'billingCountry', label: 'Billing country (EE)' },
  { key: 'billingEmail', label: 'Billing email' },
];

const STATUS_COLOR: Record<string, 'green' | 'yellow' | 'gray'> = { PAID: 'green', PENDING: 'yellow', CANCELLED: 'gray' };

/** "12.34" / "12,34" → 1234; anything else NaN. */
export function toCents(text: string): number {
  const n = Number(text.trim().replace(',', '.'));
  return Number.isFinite(n) ? Math.round(n * 100) : NaN;
}

/** Changed fields only, so an untouched form is not a write. */
export function invoicePatch(r: InvoiceRow, read: (id: string) => string): InvoicePatch {
  const out: InvoicePatch = {};
  const keys: Array<keyof InvoicePatch> = RECIPIENT_FIELDS.map((f) => f.key);
  if (isManual(r)) keys.push('description', 'status');
  for (const key of keys) {
    let next = read(`adm-inv-${key}`).trim();
    if (key === 'billingCountry') next = next.toUpperCase();
    const before = String((r as unknown as Record<string, unknown>)[key] ?? '');
    if (next !== before) out[key] = next;
  }
  return out;
}

export async function mountInvoices(container: HTMLElement): Promise<() => void> {
  let alive = true;
  let rows: InvoiceRow[] = [];
  let page = 1;
  let totalPages = 1;
  let search = '';
  let debounce: ReturnType<typeof setTimeout> | undefined;
  let playerDebounce: ReturnType<typeof setTimeout> | undefined;

  const { $, read, say, run } = scopedHelpers(container);

  async function load(): Promise<void> {
    if (!alive) return;
    try {
      const res = await getInvoices({ page, search: search || undefined });
      rows = res.items;
      totalPages = Math.ceil(res.total / res.pageSize) || 1;
    } catch {
      container.innerHTML = '<p class="text-red-400 p-4">Failed to load invoices.</p>';
      return;
    }
    renderList();
  }

  // ─── List + generate + settings ───────────────────────────────────────────

  function renderList(): void {
    const tableRows = rows.map(r => [
      esc(r.number),
      r.creditNoteForInvoiceId ? badge('Credit note', 'yellow') : r.type === 'IN' ? badge('Deposit', 'green') : badge('Payout', 'blue'),
      esc(r.user?.email ?? r.userId),
      `${formatCents(r.totalCents ?? r.amountCents)} ${esc(r.currency)}`,
      badge(r.status, STATUS_COLOR[r.status] ?? 'gray'),
      formatDate(r.issuedAt ?? r.createdAt),
      `<button class="btn btn-stone min-h-11 px-3 text-xs" data-edit-inv="${esc(r.number)}">Edit</button>`,
    ]);

    container.innerHTML = `
      <div class="flex flex-wrap items-center justify-between gap-2 mb-4">
        <h2 class="font-heading text-gold text-lg">Invoices</h2>
        <div class="flex flex-wrap gap-2">
          <button id="adm-inv-settings-toggle" class="btn btn-stone min-h-11 px-4 text-sm">Numbering</button>
          <button id="adm-inv-new" class="btn btn-gold min-h-11 px-4 text-sm">Generate invoice</button>
        </div>
      </div>

      <div id="adm-inv-settings" class="hidden frame-pewter p-4 mb-4"></div>

      <div id="adm-inv-create" class="hidden frame-pewter p-4 mb-4">
        <h3 class="font-heading text-gold text-sm mb-3">Generate invoice</h3>
        <div class="grid gap-x-3 sm:grid-cols-2">
          <div class="sm:col-span-2">
            ${formField('Find player (email or username)', 'adm-gen-find', 'text', '', { placeholder: 'Type at least 2 characters' })}
            ${adminSelect('Player', 'adm-gen-user', [{ value: '', label: 'Search above first' }])}
          </div>
          ${adminSelect('Type', 'adm-gen-type', [{ value: 'IN', label: 'IN — sale / deposit' }, { value: 'OUT', label: 'OUT — payout' }], 'IN')}
          ${adminSelect('Status', 'adm-gen-status', [{ value: 'PAID', label: 'Paid' }, { value: 'PENDING', label: 'Pending' }], 'PAID')}
          ${formField('Amount, net (e.g. 19.99)', 'adm-gen-amount', 'text', '', { placeholder: '0.00' })}
          ${formField('Currency', 'adm-gen-currency', 'text', 'EUR')}
          ${formField('VAT %', 'adm-gen-vat', 'number', '0')}
          ${formField('Payment method', 'adm-gen-paymentMethod', 'text')}
          <div class="sm:col-span-2">${formField('Description', 'adm-gen-description', 'text', '', { required: true })}</div>
          ${formField('Recipient name', 'adm-gen-recipientName', 'text')}
          ${formField('Recipient company', 'adm-gen-recipientCompany', 'text')}
          ${formField('Company / VAT code', 'adm-gen-recipientCode', 'text')}
        </div>
        <button id="adm-gen-save" class="btn btn-gold min-h-11 px-4 text-sm">Generate</button>
        ${note('adm-gen-msg')}
      </div>

      <div class="mb-4">${searchBar('Search by number or email...', 'adm-inv-search')}</div>
      ${adminTable(['Number', 'Type', 'Player', 'Total', 'Status', 'Date', ''], tableRows)}
      ${pagination(page, totalPages)}
    `;
    wireList();
  }

  function wireList(): void {
    const input = $<HTMLInputElement>('#adm-inv-search');
    if (input) {
      input.value = search;
      input.addEventListener('input', () => {
        clearTimeout(debounce);
        debounce = setTimeout(() => { search = input.value.trim(); page = 1; void load(); }, 300);
      });
    }
    container.querySelectorAll<HTMLButtonElement>('[data-page]').forEach(btn => {
      btn.addEventListener('click', () => { page = Number(btn.dataset.page); void load(); });
    });
    container.querySelectorAll<HTMLButtonElement>('[data-edit-inv]').forEach(btn => {
      btn.addEventListener('click', () => {
        const r = rows.find((x) => x.number === btn.dataset.editInv);
        if (r) renderEdit(r);
      });
    });

    $('#adm-inv-new')?.addEventListener('click', () => {
      $('#adm-inv-create')?.classList.toggle('hidden');
      $<HTMLInputElement>('#adm-gen-find')?.focus();
    });

    $('#adm-inv-settings-toggle')?.addEventListener('click', () => {
      const box = $<HTMLElement>('#adm-inv-settings');
      if (!box) return;
      box.classList.toggle('hidden');
      if (!box.classList.contains('hidden') && !box.dataset.loaded) void loadSettings(box);
    });

    const find = $<HTMLInputElement>('#adm-gen-find');
    find?.addEventListener('input', () => {
      clearTimeout(playerDebounce);
      playerDebounce = setTimeout(() => void findPlayers(find.value.trim()), 300);
    });

    $('#adm-gen-save')?.addEventListener('click', () => {
      void run($<HTMLButtonElement>('#adm-gen-save'), 'adm-gen-msg', async () => {
        const userId = read('adm-gen-user');
        if (!userId) throw new Error('Pick a player');
        const amountCents = toCents(read('adm-gen-amount'));
        if (!Number.isInteger(amountCents) || amountCents < 1) throw new Error('Enter an amount above 0');
        const description = read('adm-gen-description').trim();
        if (!description) throw new Error('Enter a description');
        const vat = Number(read('adm-gen-vat') || 0);
        if (!Number.isInteger(vat) || vat < 0 || vat > 100) throw new Error('VAT is a whole percent, 0-100');
        const opt = (id: string) => read(id).trim() || undefined;
        const inv = await generateInvoice({
          userId,
          type: read('adm-gen-type') as 'IN' | 'OUT',
          status: read('adm-gen-status') as 'PAID' | 'PENDING',
          amountCents,
          description,
          currency: (read('adm-gen-currency').trim() || 'EUR').toUpperCase(),
          vatRate: vat,
          paymentMethod: opt('adm-gen-paymentMethod'),
          recipientName: opt('adm-gen-recipientName'),
          recipientCompany: opt('adm-gen-recipientCompany'),
          recipientCode: opt('adm-gen-recipientCode'),
        });
        page = 1;
        search = '';
        await load();
        say('adm-gen-msg', '');
        const created = rows.find((x) => x.number === inv.number);
        if (created) renderEdit(created, `Issued ${inv.number}.`);
        return `Issued ${inv.number}.`;
      });
    });
  }

  async function findPlayers(q: string): Promise<void> {
    const select = $<HTMLSelectElement>('#adm-gen-user');
    if (!select) return;
    if (q.length < 2) {
      select.innerHTML = '<option value="">Search above first</option>';
      return;
    }
    let found: AdminUser[] = [];
    try { found = (await getUsers({ page: 1, per_page: 20, search: q })).items; } catch { /* shown as none */ }
    if (!select.isConnected) return;
    select.innerHTML = found.length
      ? found.map((u) => `<option value="${esc(u.id)}">${esc(u.email)}${u.username ? ` (${esc(u.username)})` : ''}</option>`).join('')
      : '<option value="">No players match</option>';
  }

  async function loadSettings(box: HTMLElement): Promise<void> {
    box.innerHTML = '<p class="text-muted text-[13px]">Loading&hellip;</p>';
    let s: InvoiceSettings;
    try { s = await getInvoiceSettings(); } catch (err) {
      box.innerHTML = `<p class="text-red-400 text-[13px]">${esc(cmsErrorText(err))}</p>`;
      return;
    }
    box.dataset.loaded = '1';
    box.innerHTML = `
      <h3 class="font-heading text-gold text-sm mb-1">Numbering &amp; delivery</h3>
      <p class="text-[11px] text-muted mb-3">A new prefix applies to the next number issued; issued numbers never change.</p>
      <div class="grid gap-x-3 sm:grid-cols-2">
        ${formField('Invoice prefix', 'adm-set-prefix', 'text', s.prefix)}
        ${formField('Credit note prefix', 'adm-set-creditNotePrefix', 'text', s.creditNotePrefix)}
        ${adminSelect('Email invoices to players', 'adm-set-emailEnabled', [{ value: 'true', label: 'On' }, { value: 'false', label: 'Off' }], String(s.emailEnabled))}
        ${formField('Re-date: days back allowed', 'adm-set-redateBackdateDays', 'number', String(s.redateBackdateDays))}
        ${formField('Re-date: hours ahead allowed', 'adm-set-redateFutureHours', 'number', String(s.redateFutureHours))}
      </div>
      <button id="adm-set-save" class="btn btn-gold min-h-11 px-4 text-sm">Save</button>
      ${note('adm-set-msg')}`;
    $('#adm-set-save')?.addEventListener('click', () => {
      void run($<HTMLButtonElement>('#adm-set-save'), 'adm-set-msg', async () => {
        await updateInvoiceSettings({
          prefix: read('adm-set-prefix').trim(),
          creditNotePrefix: read('adm-set-creditNotePrefix').trim(),
          emailEnabled: read('adm-set-emailEnabled') === 'true',
          redateBackdateDays: Number(read('adm-set-redateBackdateDays')),
          redateFutureHours: Number(read('adm-set-redateFutureHours')),
        });
        return 'Saved.';
      });
    });
  }

  // ─── One invoice ──────────────────────────────────────────────────────────

  function renderEdit(r: InvoiceRow, flash?: string): void {
    const manual = isManual(r);
    const val = (k: string) => String((r as unknown as Record<string, unknown>)[k] ?? '');
    const issued = (r.issuedAt ?? r.createdAt).slice(0, 10);

    container.innerHTML = `
      <div class="flex flex-wrap items-center gap-2 mb-4">
        <button id="adm-inv-back" class="btn btn-stone min-h-11 px-4 text-sm">&larr; Back</button>
        <a class="btn btn-stone min-h-11 px-4 text-sm inline-flex items-center" href="${esc(invoicePdfUrl(r.number))}"
          target="_blank" rel="noopener" data-no-route>Download PDF</a>
      </div>
      <div class="frame-pewter p-4 mb-4">
        <h3 class="font-heading text-gold text-lg mb-3">${esc(r.number)}</h3>
        ${flash ? `<p class="text-[13px] text-emerald-300 mb-2">${esc(flash)}</p>` : ''}
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-[13px]">
          <div><span class="text-muted">Player:</span> ${esc(r.user?.email ?? r.userId)}</div>
          <div><span class="text-muted">Type:</span> ${esc(r.type)}${r.creditNoteForInvoiceId ? ' (credit note)' : ''}</div>
          <div><span class="text-muted">Net:</span> ${formatCents(r.amountCents)} ${esc(r.currency)}</div>
          <div><span class="text-muted">VAT:</span> ${r.vatRate ?? 0}% · ${formatCents(r.vatCents ?? 0)}</div>
          <div><span class="text-muted">Total:</span> ${formatCents(r.totalCents ?? r.amountCents)} ${esc(r.currency)}</div>
          <div><span class="text-muted">Status:</span> ${badge(r.status, STATUS_COLOR[r.status] ?? 'gray')}</div>
        </div>
        <p class="text-[11px] text-muted mt-3">Amounts, VAT, currency and the number are fixed once issued.${manual ? '' : ' This invoice belongs to a payment, so its status and description follow that payment.'}</p>
      </div>

      <div class="frame-pewter p-4 mb-4">
        <h3 class="font-heading text-gold text-sm mb-3">Details</h3>
        <div class="grid gap-x-3 sm:grid-cols-2">
          ${manual ? `
            ${adminSelect('Status', 'adm-inv-status', ['PENDING', 'PAID', 'CANCELLED'].map((v) => ({ value: v, label: v })), r.status)}
            <div></div>
            <div class="sm:col-span-2">${formField('Description', 'adm-inv-description', 'text', val('description'))}</div>` : ''}
          ${RECIPIENT_FIELDS.map((f) => formField(f.label, `adm-inv-${f.key}`, f.key === 'billingEmail' ? 'email' : 'text', val(f.key))).join('')}
        </div>
        <button id="adm-inv-save" class="btn btn-gold min-h-11 px-4 text-sm">Save changes</button>
        ${note('adm-inv-save-msg')}
      </div>

      <div class="frame-pewter p-4 mb-4">
        <h3 class="font-heading text-gold text-sm mb-1">Document date</h3>
        <p class="text-[11px] text-muted mb-3">Must keep the numbering in date order; the reason goes into the player's audit log.</p>
        <div class="grid gap-x-3 sm:grid-cols-2">
          ${formField('Issued on', 'adm-inv-date', 'date', issued)}
          ${formField('Reason', 'adm-inv-date-reason', 'text', '', { placeholder: 'e.g. issued late, paid on 3 Oct' })}
        </div>
        <button id="adm-inv-date-save" class="btn btn-stone min-h-11 px-4 text-sm">Change date</button>
        ${note('adm-inv-date-msg')}
      </div>
    `;

    $('#adm-inv-back')?.addEventListener('click', () => void load());

    $('#adm-inv-save')?.addEventListener('click', () => {
      void run($<HTMLButtonElement>('#adm-inv-save'), 'adm-inv-save-msg', async () => {
        const patch = invoicePatch(r, read);
        if (!Object.keys(patch).length) return 'No changes.';
        const next = await updateInvoice(r.number, patch);
        Object.assign(r, next);
        renderEdit(r, 'Saved.');
        return 'Saved.';
      });
    });

    $('#adm-inv-date-save')?.addEventListener('click', () => {
      void run($<HTMLButtonElement>('#adm-inv-date-save'), 'adm-inv-date-msg', async () => {
        const date = read('adm-inv-date');
        const reason = read('adm-inv-date-reason').trim();
        if (!date) throw new Error('Pick a date');
        if (reason.length < 3) throw new Error('Give a reason (3+ characters)');
        const next = await redateInvoice(r.number, date, reason);
        Object.assign(r, next);
        renderEdit(r, `Re-dated to ${date}.`);
        return 'Re-dated.';
      });
    });
  }

  await load();
  return () => { alive = false; clearTimeout(debounce); clearTimeout(playerDebounce); };
}
